import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  TextField,
  Button,
  Typography,
  Chip,
  Box,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import EditIcon from "@material-ui/icons/Edit";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Field, Form, Formik } from "formik";
import {
  ROOM_TYPES,
  BED_TYPES,
  AMENITIES_LIST,
  RULES_LIST,
} from "src/utils/misc";
import { updateRoomDetails } from "src/actions/HomeActions";

const TOKENS = {
  surface: "#FFFFFF",
  border: "#EEF0F5",
  textPrimary: "#0F172A",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  accent: "#222222",
  accentDark: "#000000",
  divider: "#F1F5F9",
};

const useStyles = makeStyles(() => ({
  section: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    marginBottom: 20,
    overflow: "hidden",
  },
  sectionHeader: {
    padding: "20px 24px 0 24px",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: TOKENS.textPrimary,
  },
  sectionSubtitle: {
    fontSize: 13,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  sectionContent: {
    padding: "16px 24px 24px 24px",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 14,
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#A5B4FC" },
      "&.Mui-focused fieldset": { borderColor: TOKENS.accent },
    },
    "& .MuiInputLabel-root": { fontSize: 14, color: TOKENS.textSecondary },
  },
  selectField: {
    borderRadius: 8,
    fontSize: 14,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E8F0" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#A5B4FC" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: TOKENS.accent,
    },
  },
  chipSelected: {
    backgroundColor: `${TOKENS.accent} !important`,
    color: "#FFFFFF !important`,",
    borderColor: `${TOKENS.accent} !important`,
    fontWeight: 500,
  },
  chipUnselected: {
    backgroundColor: "#FFFFFF",
    color: "#475569",
    borderColor: "#E2E8F0",
    fontWeight: 400,
    "&:hover": { backgroundColor: "#F8FAFC", borderColor: "#A5B4FC" },
  },
  chipGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  bedRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    backgroundColor: "#FAFBFC",
    borderRadius: 10,
    border: `1px solid ${TOKENS.border}`,
    marginBottom: 8,
  },
  varianceRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    backgroundColor: "#FAFBFC",
    borderRadius: 10,
    border: `1px solid ${TOKENS.border}`,
    marginBottom: 8,
  },
  addRowBtn: {
    color: TOKENS.accent,
    borderColor: TOKENS.border,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    "&:hover": { borderColor: "#A5B4FC", backgroundColor: "#EEF2FF" },
  },
  // Room list card
  roomCard: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    marginBottom: 12,
    overflow: "hidden",
    transition: "border-color 0.15s ease",
    "&:hover": { borderColor: "#A5B4FC" },
  },
  roomCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: `1px solid ${TOKENS.divider}`,
  },
  roomName: {
    fontSize: 15,
    fontWeight: 600,
    color: TOKENS.textPrimary,
  },
  roomMeta: {
    fontSize: 13,
    color: TOKENS.textSecondary,
    marginTop: 2,
  },
  roomCardBody: {
    padding: "14px 20px",
  },
  roomStat: {
    fontSize: 13,
    color: TOKENS.textSecondary,
    marginBottom: 4,
    "& strong": { color: TOKENS.textPrimary, fontWeight: 600 },
  },
  roomThumb: {
    width: "100%",
    height: 100,
    objectFit: "cover",
    borderRadius: 8,
    border: `1px solid ${TOKENS.border}`,
  },
  updateRoomBtn: {
    color: TOKENS.accent,
    borderColor: `${TOKENS.accent}40`,
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "none",
    padding: "6px 14px",
    "&:hover": { borderColor: TOKENS.accent, backgroundColor: "#EEF2FF" },
  },
  addRoomBtn: {
    color: TOKENS.accent,
    borderColor: TOKENS.border,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    marginBottom: 20,
    "&:hover": { borderColor: "#A5B4FC", backgroundColor: "#EEF2FF" },
  },
  saveButton: {
    backgroundColor: TOKENS.accent,
    color: "#FFFFFF",
    borderRadius: 8,
    padding: "12px 32px",
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "0 1px 2px rgba(34, 34, 34, 0.3)",
    "&:hover": { backgroundColor: TOKENS.accentDark },
    "&:disabled": { backgroundColor: "#C7D2FE", color: "#FFFFFF" },
  },
  backButton: {
    color: TOKENS.textSecondary,
    borderColor: TOKENS.border,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    textTransform: "none",
    marginRight: 10,
    "&:hover": { borderColor: "#A5B4FC", backgroundColor: TOKENS.divider },
  },
  imageSelectSection: {
    backgroundColor: TOKENS.surface,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 16,
    boxShadow: "none",
    marginBottom: 20,
    overflow: "hidden",
  },
  imageThumb: {
    width: "100%",
    height: 120,
    objectFit: "cover",
    borderRadius: 8,
    cursor: "pointer",
    transition: "transform 0.15s ease",
    "&:hover": { transform: "scale(1.03)" },
  },
  noImages: {
    fontSize: 13,
    color: TOKENS.textMuted,
    fontStyle: "italic",
  },
}));

export default function RoomsEditTab() {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hotel } = useSelector((state) => state.homes);

  const [currentRoom, setCurrentRoom] = useState(null);
  const [roomDetails, setRoomDetails] = useState({
    room_type: "",
    room_type_count: 1,
    room_name: "",
    number_of_guests: "",
    base_price: "",
    room_description: "",
  });
  const [roomBeds, setRoomBeds] = useState([{ bed_type: "", bed_count: 1 }]);
  const [priceVariance, setPriceVariance] = useState([]);
  const [roomAmenities, setRoomAmenities] = useState([]);
  const [roomRules, setRoomRules] = useState([]);
  const [roomImages, setRoomImages] = useState([]);

  const toggleAmenity = (name) => {
    setRoomAmenities((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const toggleRule = (name) => {
    setRoomRules((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name],
    );
  };

  const handleSelectRoom = (room) => {
    setCurrentRoom(room.id);
    setRoomDetails({
      room_type: room.room_type,
      room_type_count: room.room_type_count,
      number_of_guests: room.number_of_guests,
      base_price: room.base_price,
      room_description: room.room_description,
    });
    setRoomBeds(
      room.room_beds.map((item) => ({
        bed_type: item.name,
        bed_count: item.count,
      })),
    );
    setRoomRules(room.room_rules.map((item) => item.name));
    setRoomAmenities(room.room_amenities.map((item) => item.name));
    setRoomImages(room.room_images.map((item) => item));
    setPriceVariance(
      room?.price_variance
        ? room.price_variance.map((item) => ({
            guestCount: item.guest_count,
            price: item.price,
          }))
        : [],
    );
  };

  const handleCancel = () => {
    setCurrentRoom(null);
    setRoomDetails({
      room_type: "",
      room_type_count: 1,
      room_name: "",
      number_of_guests: "",
      base_price: "",
      room_description: "",
    });
    setRoomBeds([{ bed_type: "", bed_count: 1 }]);
    setRoomRules([]);
    setRoomAmenities([]);
    setRoomImages([]);
    setPriceVariance([]);
  };

  // ── Room list view ──────────────────────────────────────────────────────────
  if (!currentRoom) {
    return (
      <>
        <Button
          variant="outlined"
          className={classes.addRoomBtn}
          startIcon={<AddIcon style={{ fontSize: 16 }} />}
          onClick={() => navigate("/app/hotel/edit/addRoom")}
        >
          Add New Room
        </Button>

        {hotel?.rooms.map((room) => (
          <Card key={room.id} className={classes.roomCard}>
            <Box className={classes.roomCardHeader}>
              <Box>
                <Typography className={classes.roomName}>
                  {room.room_name}
                </Typography>
                <Typography className={classes.roomMeta}>
                  {room.room_type} · {room.room_type_count} room
                  {room.room_type_count > 1 ? "s" : ""}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                className={classes.updateRoomBtn}
                startIcon={<EditIcon style={{ fontSize: 14 }} />}
                onClick={() => handleSelectRoom(room)}
              >
                Edit Room
              </Button>
            </Box>
            <Box className={classes.roomCardBody}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography className={classes.roomStat}>
                    <strong>Base Price:</strong> USD {room.base_price}
                  </Typography>
                  <Typography className={classes.roomStat}>
                    <strong>Max Guests:</strong> {room.number_of_guests}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  {room.room_images.length > 0 ? (
                    <Grid container spacing={1}>
                      {room.room_images.slice(0, 5).map((image) => (
                        <Grid item xs={3} sm={2} key={image._id}>
                          <img
                            src={image.image_url}
                            alt="Room"
                            className={classes.roomThumb}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Typography className={classes.noImages}>
                      No images uploaded for this room
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Card>
        ))}
      </>
    );
  }

  // ── Edit form view ──────────────────────────────────────────────────────────
  return (
    <>
      {/* Image selector */}
      <Card className={classes.imageSelectSection}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>Room Images</Typography>
          <Typography className={classes.sectionSubtitle}>
            Tap images to select or deselect them for this room —{" "}
            {roomImages.length} selected
          </Typography>
        </Box>
        <CardContent className={classes.sectionContent}>
          <Grid container spacing={1}>
            {hotel?.otherImages.map((image) => {
              const isSelected = roomImages.some(
                (img) => img.image_url === image.image_url,
              );
              return (
                <Grid item xs={3} sm={2} key={image.image_url}>
                  <Box style={{ position: "relative" }}>
                    <img
                      src={image.image_url}
                      alt="Room"
                      className={classes.imageThumb}
                      style={{
                        border: isSelected
                          ? `3px solid ${TOKENS.accent}`
                          : `2px solid ${TOKENS.border}`,
                      }}
                      onClick={() =>
                        setRoomImages((prev) =>
                          isSelected
                            ? prev.filter(
                                (img) => img.image_url !== image.image_url,
                              )
                            : [...prev, image],
                        )
                      }
                    />
                    {isSelected && (
                      <Box
                        style={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          width: 18,
                          height: 18,
                          borderRadius: 9,
                          backgroundColor: TOKENS.accent,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span
                          style={{
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          ✓
                        </span>
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      <Formik
        initialValues={roomDetails}
        onSubmit={async (values, { resetForm }) => {
          const obj = {
            ...values,
            id: currentRoom,
            room_type: roomDetails.room_type,
            room_name: roomDetails.room_type,
            room_amenities: roomAmenities.map((name) => ({ name })),
            room_beds: roomBeds.map((r) => ({
              name: r.bed_type,
              count: r.bed_count,
            })),
            room_rules: roomRules.map((name) => ({ name })),
            price_variance: priceVariance.map((item) => ({
              guest_count: item.guestCount,
              price: item.price,
            })),
            base_price: parseFloat(values.base_price),
            room_images: roomImages,
          };
          dispatch(updateRoomDetails(obj, hotel.id));
          handleCancel();
          resetForm();
        }}
      >
        {({ handleBlur, handleChange, handleSubmit, isSubmitting }) => (
          <Form noValidate onSubmit={handleSubmit}>
            {/* Room Details */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Room Details
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Type, count, capacity, and description
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl
                      variant="outlined"
                      fullWidth
                      className={classes.textField}
                    >
                      <InputLabel>Room Type</InputLabel>
                      <Select
                        className={classes.selectField}
                        onChange={(e) =>
                          setRoomDetails({
                            ...roomDetails,
                            room_type: e.target.value,
                          })
                        }
                        label="Room Type"
                        value={roomDetails.room_type}
                      >
                        {ROOM_TYPES.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="How Many Rooms?"
                      name="room_type_count"
                      type="number"
                      inputProps={{ min: 1 }}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Max Guests"
                      name="number_of_guests"
                      type="number"
                      inputProps={{ min: 1 }}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      multiline
                      minRows={2}
                      label="Room Description"
                      name="room_description"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      placeholder="Describe the room and what makes it comfortable"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Beds */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Bed Configuration
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  What beds are available in this room?
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                {roomBeds.map((bed, index) => (
                  <Box key={index} className={classes.bedRow}>
                    <FormControl
                      variant="outlined"
                      style={{ flex: 1 }}
                      className={classes.textField}
                    >
                      <InputLabel>Bed Type</InputLabel>
                      <Select
                        className={classes.selectField}
                        onChange={(e) => {
                          const updated = [...roomBeds];
                          updated[index].bed_type = e.target.value;
                          setRoomBeds(updated);
                        }}
                        label="Bed Type"
                        value={bed.bed_type}
                      >
                        {BED_TYPES.map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      className={classes.textField}
                      variant="outlined"
                      label="Count"
                      type="number"
                      inputProps={{ min: 1 }}
                      value={bed.bed_count}
                      onChange={(e) => {
                        const updated = [...roomBeds];
                        updated[index].bed_count = e.target.value;
                        setRoomBeds(updated);
                      }}
                      style={{ width: 100 }}
                    />
                    {roomBeds.length > 1 && (
                      <IconButton
                        size="small"
                        style={{ color: "#EF4444" }}
                        onClick={() =>
                          setRoomBeds(roomBeds.filter((_, i) => i !== index))
                        }
                      >
                        <RemoveIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    )}
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  className={classes.addRowBtn}
                  startIcon={<AddIcon style={{ fontSize: 16 }} />}
                  onClick={() =>
                    setRoomBeds([...roomBeds, { bed_type: "", bed_count: 1 }])
                  }
                >
                  Add Another Bed
                </Button>
              </CardContent>
            </Card>

            {/* Amenities */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Room Amenities
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  What can guests use in this room? —{" "}
                  {roomAmenities.length > 0
                    ? `${roomAmenities.length} selected`
                    : "none selected"}
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Box className={classes.chipGrid}>
                  {AMENITIES_LIST.map((name) => (
                    <Chip
                      key={name}
                      label={roomAmenities.includes(name) ? `✓ ${name}` : name}
                      clickable
                      variant="outlined"
                      size="small"
                      className={
                        roomAmenities.includes(name)
                          ? classes.chipSelected
                          : classes.chipUnselected
                      }
                      onClick={() => toggleAmenity(name)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Rules */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Room Rules
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Set expectations for this room —{" "}
                  {roomRules.length > 0
                    ? `${roomRules.length} selected`
                    : "none selected"}
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Box className={classes.chipGrid}>
                  {RULES_LIST.map((name) => (
                    <Chip
                      key={name}
                      label={roomRules.includes(name) ? `✓ ${name}` : name}
                      clickable
                      variant="outlined"
                      size="small"
                      className={
                        roomRules.includes(name)
                          ? classes.chipSelected
                          : classes.chipUnselected
                      }
                      onClick={() => toggleRule(name)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card className={classes.section}>
              <Box className={classes.sectionHeader}>
                <Typography className={classes.sectionTitle}>
                  Pricing
                </Typography>
                <Typography className={classes.sectionSubtitle}>
                  Base rate and optional price variance by guest count
                </Typography>
              </Box>
              <CardContent className={classes.sectionContent}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Field
                      as={TextField}
                      className={classes.textField}
                      variant="outlined"
                      fullWidth
                      label="Base Price per Night (USD)"
                      name="base_price"
                      type="number"
                      inputProps={{ min: 1 }}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Grid>
                </Grid>

                {priceVariance.length > 0 && (
                  <Box style={{ marginTop: 16 }}>
                    <Typography
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: TOKENS.textSecondary,
                        marginBottom: 8,
                      }}
                    >
                      Price Variance by Guest Count
                    </Typography>
                    {priceVariance.map((item, index) => (
                      <Box key={index} className={classes.varianceRow}>
                        <TextField
                          className={classes.textField}
                          variant="outlined"
                          label="Guests"
                          type="number"
                          inputProps={{ min: 1 }}
                          value={item.guestCount}
                          onChange={(e) => {
                            const updated = [...priceVariance];
                            updated[index].guestCount = e.target.value;
                            setPriceVariance(updated);
                          }}
                          style={{ width: 120 }}
                        />
                        <TextField
                          className={classes.textField}
                          variant="outlined"
                          label="Price (USD)"
                          type="number"
                          inputProps={{ min: 1 }}
                          value={item.price}
                          onChange={(e) => {
                            const updated = [...priceVariance];
                            updated[index].price = e.target.value;
                            setPriceVariance(updated);
                          }}
                          style={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          style={{ color: "#EF4444" }}
                          onClick={() =>
                            setPriceVariance(
                              priceVariance.filter((_, i) => i !== index),
                            )
                          }
                        >
                          <RemoveIcon style={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box style={{ marginTop: 12 }}>
                  <Button
                    variant="outlined"
                    className={classes.addRowBtn}
                    startIcon={<AddIcon style={{ fontSize: 16 }} />}
                    onClick={() =>
                      setPriceVariance([
                        ...priceVariance,
                        { guestCount: 1, price: "" },
                      ])
                    }
                  >
                    Add Price Variance
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Actions */}
            <Box
              display="flex"
              justifyContent="flex-end"
              style={{ marginTop: 8 }}
            >
              <Button
                type="button"
                variant="outlined"
                className={classes.backButton}
                startIcon={<ArrowBackIcon />}
                onClick={handleCancel}
              >
                Back to Rooms
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                variant="contained"
                className={classes.saveButton}
                endIcon={<SaveIcon />}
              >
                Save Room
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
}
